import React, {
    useContext, useEffect, useState
} from 'react';
import parse, { domToReact } from 'html-react-parser';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Select } from '@ferris/react/components/forms';
import Input from '@ferris/react/components/forms/Input';
import classnames from 'classnames';
import { hot } from 'react-hot-loader';
import { Icon } from '@ferris/react';
import { Popover, PopoverBody } from '@ferris/react/components/popover';
import { TrackingContext } from '@vrbo/react-event-tracking';
import LeadActions from '../../../actions/lead';
import AppActions from '../../../actions/app';
import TranslationUtil from '../../../utils/TranslationUtil';
import LeadFormSubmit from './LeadFormSubmit';
import withPageFlow from '../../PageFlow/PageFlow';
import PhoneInputWrapper from '../../Input/PhoneInputWrapper';
import NewsLetter from './NewsLetter';
import { getAbovCookie } from '../../../utils/CookieUtil';
import ProgressBarLYP from '../../ProgressBarLYP';
import LeadCreateErrorAlert from './LeadCreateErrorAlert';
import { getOptOutState } from '../../../utils/NewsLetterUtil';
import { latinName, unicodeRegexname } from '../../../middleware/validation/rules';
import { createImpressionEvent, createInteractionEvent, createPageViewEvent } from '../../../utils/EventProviders/EgClickstreamUtil';
import ExperimentUtil from '../../../utils/ExperimentUtil';
import { isEnUsPermanentFeatureVrEnabled } from '../../../utils/ExperimentsUtil';
import { buildAddPropertyPath } from '../../../utils/pathUtil';
import { PHONE_VALIDATION_ERROR_MESSAGE } from '../../../constants/common';
import { capitalizeBrand } from '../../../utils/BrandUtil';
import { scrollToView } from '../../../../server/utils/UIUtil';
import { isPropertyNameValid } from '../PropertyDetails/helpers/propertyNameValidation';
import noopAnalytics from '../../../utils/noopAnalytics';
import CompetitorLink from '../EstimateResults/CompetitorLink';
import { ExperimentCtx } from '../../../contexts/experiments';
import { EGTNL_TESTS_MPAAS } from '../../../../server/api/abacus/constants';

const getFormInput = (
    forItem,
    label,
    type,
    value,
    changeHandler,
    error,
    handleBlur,
    handleFocus
) => (
    <Input
        formControlProps={{ id: `${forItem}-input` }}
        classes={`${forItem}-input`}
        fullWidth
        label={label}
        type={type}
        isControlled
        floatLabel
        data-wdio={`${forItem}-input`}
        value={value}
        onChange={changeHandler}
        invalid={error && (['array', 'string'].includes(typeof error) ? error.length : true)}
        validationMessage={error || ''}
        onBlur={handleBlur}
        onFocus={handleFocus}
        aria-label={label}
        aria-invalid={!!error}
    />
);

export const surnameFirstLocale = ['ko_KR', 'zh_CN', 'ja_JP', 'zh_TW', 'he_IL', 'ar_SA'];

const isEmailValidFromService = (props) => {
    const { emailValidation } = props;
    const { validationService = {} } = emailValidation || {};
    return validationService && validationService.success && validationService.isValid;
};

const isPhoneValidFromService = (props) => {
    const { phoneValidation } = props;
    const { validationService = {} } = phoneValidation || {};
    return validationService && validationService.success && validationService.isValid;
};

const isNameLocal = (name) => RegExp(unicodeRegexname).test(name)
    && !RegExp(latinName).test(name);

const isContactNameLocal = (fName, lName) => isNameLocal(fName) || isNameLocal(lName);

const isValidContactName = (props) => {
    const {
        firstNameValidation,
        lastNameValidation,
        latinFirstNameValidation,
        latinLastNameValidation,
        firstName,
        lastName,
        features
    } = props;
    if (features.isConventionalLodgingFlow) {
        if (isContactNameLocal(firstName, lastName)) {
            return firstNameValidation.isValid
                && lastNameValidation.isValid
                && latinFirstNameValidation.isValid
                && latinLastNameValidation.isValid;
        }
        return firstNameValidation.isValid
            && lastNameValidation.isValid;
    }
    return firstNameValidation.isValid
        && lastNameValidation.isValid;
};

export const isValidPropertyName = (props) => {
    const {
        features,
        propertyNameValidation,
        latinPropertyNameValidation,
        propertyName,
        latinPropertyName,
        locale
    } = props;
    if (!features.isConventionalLodgingFlow) return true;
    return isPropertyNameValid({
        locale,
        propertyName,
        latinPropertyName,
        propertyNameValidation,
        latinPropertyNameValidation
    });
};

export const isFormDataValid = (props) => {
    const {
        emailValidation,
        phoneValidation,
        passwordValidation,
        isServerEmailValidationEnabled,
        features
    } = props;
    const resultCl = (
        emailValidation.isValid
        && phoneValidation.isValid
        && isPhoneValidFromService(props)
        && isValidContactName(props)
        && isValidPropertyName(props)
    );
    const resultFrbo = resultCl
        && passwordValidation.isValid
        && (isServerEmailValidationEnabled ? isEmailValidFromService(props) : true);
    return features.isFrboFlow ? resultFrbo : resultCl;
};
const GLANCE_MASKED = { glance_masked: 'true' };

export function LeadForm(props) {
    const { trigger } = useContext(TrackingContext);

    const {
        initialEmailValidation,
        initialFirstNameValidation,
        initialLatinFirstNameValidation,
        initialLastNameValidation,
        initialLatinLastNameValidation,
        titleComponent
    } = props;

    const [emailValidationEnabled, setEmailValidationEnabled] = useState(initialEmailValidation);
    const [firstNameValidationEnabled, setFirstNameValidationEnabled] = useState(
        initialFirstNameValidation
    );
    const [latinFirstNameValidationEnabled, setLatinFirstNameValidationEnabled] = useState(
        initialLatinFirstNameValidation
    );
    const [lastNameValidationEnabled, setLastNameValidationEnabled] = useState(
        initialLastNameValidation
    );
    const [latinLastNameValidationEnabled, setLatinLastNameValidationEnabled] = useState(
        initialLatinLastNameValidation
    );

    useEffect(() => {
        scrollToView();
        const {
            referrer, analytics, features, pageTitle, clearAccountCreationError
        } = props;

        if (features.isConventionalLodgingFlow && typeof window === 'object') {
            window.document.title = pageTitle;
        }
        trigger('pageLoad', { pageName: 'partner-qualifier: lead-capture', referrer });
        trigger('pageview', createPageViewEvent('Leadcapture'));
        analytics.setAnalyticsPageName('JoinFormContactInfo');
        analytics.viewTTIstop();
        trigger('pageLoadGtm', { pageName: 'PropertyContact', event: 'PropertyContactLoad' });

        return () => {
            trigger('pageUnload', { referrer });
            clearAccountCreationError();
            analytics.viewTTIstart();
        };
    }, []);

    const handleEmailBlur = (e) => {
        const { setEmail } = props;
        setEmail(e.target.value);
    };

    const handleTitleNameChange = (e) => {
        const { setTitleName } = props;
        setTitleName(e.target.value);
    };

    const handleFirstNameChange = (e) => {
        const { setFirstName } = props;
        setFirstName(e.target.value);
    };

    const handleFirstNameBlur = () => {
        setFirstNameValidationEnabled(true);
    };

    const handleLastNameChange = (e) => {
        const { setLastName } = props;
        setLastName(e.target.value);
    };

    const handleLastNameBlur = () => {
        setLastNameValidationEnabled(true);
    };

    const handleLatinFirstNameChange = (e) => {
        const { setLatinFirstName } = props;
        setLatinFirstName(e.target.value);
    };

    const handleLatinFirstNameBlur = () => {
        setLatinFirstNameValidationEnabled(true);
    };

    const handleLatinLastNameChange = (e) => {
        const { setLatinLastName } = props;
        setLatinLastName(e.target.value);
    };

    const handleLatinLastNameBlur = () => {
        setLatinLastNameValidationEnabled(true);
    };

    const handlePasswordChange = (e) => {
        const { setPassword } = props;
        setPassword(e.target.value);
    };

    const css = classnames('ulx-form', 'ulx-animated');

    const handleLoginClick = () => {
        trigger('interaction', createInteractionEvent('login.selected', 'Leadcapture', 'leadForm', 'LeadLoginForm'));
        trigger('genericClick', {
            category: 'navigation',
            action: 'Lead: loginButton'
        });
    };

    const renderLoginLink = (str) => {
        const options = {
            replace: ({ attribs, name, children }) => {
                if (!attribs) return;
                if (name === 'a') {
                    // eslint-disable-next-line consistent-return
                    return (
                        <a href={attribs.href} onClick={handleLoginClick}>
                            {domToReact(children)}
                        </a>
                    );
                }
            }
        };
        return parse(str, options);
    };

    // first name validation messaging
    const {
        firstNameValidation
    } = props;
    const [fnValidationMessage, setFnValidationMessage] = useState('');
    useEffect(() => {
        if (firstNameValidationEnabled) {
            setFnValidationMessage(firstNameValidation.error);
        } else {
            setFnValidationMessage('');
        }
    }, [firstNameValidation.error, firstNameValidationEnabled]);

    // last name validation messaging
    const { lastNameValidation } = props;
    const [lnValidationMessage, setLnValidationMessage] = useState('');
    useEffect(() => {
        if (lastNameValidationEnabled) {
            setLnValidationMessage(lastNameValidation.error);
        } else {
            setLnValidationMessage('');
        }
    }, [lastNameValidation.error, lastNameValidationEnabled]);

    // Latin First name validation messaging
    const { latinFirstNameValidation } = props;
    const [lfnValidationMessage, setLfnValidationMessage] = useState('');
    useEffect(() => {
        if (latinFirstNameValidationEnabled) {
            setLfnValidationMessage(latinFirstNameValidation.error);
        } else {
            setLfnValidationMessage('');
        }
    }, [latinFirstNameValidation.error, latinFirstNameValidationEnabled]);

    // Latin Last name validation messaging
    const { latinLastNameValidation } = props;
    const [llnValidationMessage, setLlnValidationMessage] = useState('');
    useEffect(() => {
        if (latinLastNameValidationEnabled) {
            setLlnValidationMessage(latinLastNameValidation.error);
        } else {
            setLlnValidationMessage('');
        }
    }, [latinLastNameValidation.error, latinLastNameValidationEnabled]);

    const {
        emailValidation,
        validateEmail,
        isDoubledValidation,
        isServerEmailValidationEnabled,
        email
    } = props;

    useEffect(() => {
        setEmailValidationEnabled(true);
        if ((isServerEmailValidationEnabled || isDoubledValidation) && emailValidation.isValid) {
            validateEmail(email, isDoubledValidation, isServerEmailValidationEnabled);
        }
    }, [email, emailValidation.isValid]);

    // email validation messaging
    const {
        emailValidationErrorText,
        emailDuplicateErrorText,
        emailManagedErrorText,
        features
    } = props;
    const { validationService = {} } = emailValidation || {};
    const [emailValidationMessage, setEmailValidationMessage] = useState('');
    useEffect(() => {
        // eslint-disable-next-line max-len
        const isEmailInvalidFromService = validationService && validationService.success && !validationService.isValid;
        let newMessage;
        if (emailValidation.error || isEmailInvalidFromService) {
            const errorFromService = features.isFrboFlow
                && isServerEmailValidationEnabled
                && isEmailInvalidFromService
                ? emailValidationErrorText
                : undefined;
            switch (emailValidation.error) {
            case 'validation.error.isDuplicateEmail':
                try {
                    trigger('impression', createImpressionEvent('error_message.presented', 'Leadcapture', 'EmailDuplicate'));
                } catch (e) {} // eslint-disable-line no-empty
                newMessage = renderLoginLink(emailDuplicateErrorText);
                break;
            case 'validation.error.isManagedEmail':
                newMessage = renderLoginLink(emailManagedErrorText);
                break;
            case 'email.validation.failure.default':
            case 'Couldn\'t validate email with kickbox':
                newMessage = emailValidationErrorText;
                break;
            default:
                newMessage = emailValidation.error || errorFromService;
                break;
            }
        }
        setEmailValidationMessage(newMessage);
    }, [emailValidationEnabled,
        emailValidation.error,
        validationService.success,
        validationService.isValid
    ]);

    const {
        existingUserText,
        loginText,
        newAccountText,
        firstNameLabel,
        lastNameLabel,
        emailLabel,
        passwordLabel,
        termsText,
        titleName,
        firstName,
        lastName,
        latinFirstName,
        latinLastName,
        password,
        showLeadCreationError,
        authUrl,
        shouldShowTitleDropdown,
        step,
        latinRomanTooltip,
        locale
    } = props;

    const contactType = features.isFrboFlow ? 'FRBO' : 'HOTELIER';

    // password validation messaging
    const { passwordValidation } = props;
    const [passwordValidationMessage, setPasswordValidationMessage] = useState('');
    useEffect(() => {
        setPasswordValidationMessage(passwordValidation.error);
    }, [passwordValidation.error]);

    const passwordField = (message) => (
        <div className="ulx-form__form__input password-field" {...GLANCE_MASKED}>
            {getFormInput('password', passwordLabel, 'password', password, handlePasswordChange, message)}
        </div>
    );

    const emailField = (
        <div className="ulx-form__form__input" {...GLANCE_MASKED}>
            {/* email validation */}
            {getFormInput('email-address', emailLabel, 'email', email, () => {}, emailValidationMessage, handleEmailBlur)}
        </div>
    );

    const onSubmitForm = (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const { 'fc-token': fcToken } = Object.fromEntries(formData);
        window['fc-token'] = fcToken;
    };
    const { competitorLink, propertyCreationText } = props;

    const {
        experimentEvaluations
    } = useContext(ExperimentCtx) || {};

    const isEstimateHidden = experimentEvaluations?.[EGTNL_TESTS_MPAAS.ULX_MKTING_MIGRATION] === 1;
    return (
        <form
            id="lead-form-submit"
            className={css}
            data-wdio="ulx-lead-form"
            onSubmit={onSubmitForm}
        >
            <ProgressBarLYP step={step} />
            {titleComponent()}
            {
                features.isConventionalLodgingFlow ? (
                    <p className="ulx-form__login">
                        {renderLoginLink(loginText)}
                    </p>
                ) : (
                    <>
                        <p className="ulx-form__login">
                            {existingUserText}
                            {' '}
                            {renderLoginLink(loginText)}
                        </p>
                        <p className="ulx-form__login">
                            {newAccountText}
                        </p>
                    </>
                )
            }
            <div className="ulx-form__form fs-block">
                <div className="ulx-form-area">
                    {showLeadCreationError && (
                        <LeadCreateErrorAlert
                            authUrl={authUrl}
                            isConventionalLodgingFlow={features.isConventionalLodgingFlow}
                        />
                    )}
                    <div className="ulx-form__form__name">
                        {shouldShowTitleDropdown && (
                            <div className="ulx-form__form__name__selectFill">
                                <Select
                                    fullWidth
                                    name="title-name"
                                    formControlProps={
                                        { id: 'title-name' }
                                    }
                                    value={titleName}
                                    label="Anrede"
                                    onChange={handleTitleNameChange}
                                    floatLabel
                                    isControlled
                                    optionList={[
                                        {
                                            text: '',
                                            value: undefined
                                        },
                                        {
                                            text: 'Herr',
                                            value: 'Herr'
                                        },
                                        {
                                            text: 'Frau',
                                            value: 'Frau'
                                        },
                                        {
                                            text: 'Firma',
                                            value: 'Firma'
                                        }
                                    ]}
                                />
                            </div>
                        )}
                        {surnameFirstLocale.includes(locale) === true && (
                            <div className="ulx-form__form__name__outer" {...GLANCE_MASKED}>
                                <div className="ulx-form__form__name__firstFill">
                                    {getFormInput('last-name', lastNameLabel, 'text', lastName, handleLastNameChange, lnValidationMessage, handleLastNameBlur)}
                                </div>
                                <div className="ulx-form__form__name__lastFill">
                                    {getFormInput('first-name', firstNameLabel, 'text', firstName, handleFirstNameChange, fnValidationMessage, handleFirstNameBlur)}
                                </div>
                            </div>
                        )}

                        {surnameFirstLocale.includes(locale) === false && (
                            <div className="ulx-form__form__name__outer" {...GLANCE_MASKED}>
                                <div className="ulx-form__form__name__firstFill">
                                    {getFormInput('first-name', firstNameLabel, 'text', firstName, handleFirstNameChange, fnValidationMessage, handleFirstNameBlur)}
                                </div>
                                <div className="ulx-form__form__name__lastFill">
                                    {getFormInput('last-name', lastNameLabel, 'text', lastName, handleLastNameChange, lnValidationMessage, handleLastNameBlur)}
                                </div>
                            </div>
                        )}

                    </div>
                    {features.isConventionalLodgingFlow
                        && isContactNameLocal(firstName, lastName)
                        && (
                            <div>
                                <div className="ulx-form__form__nameTooltip">
                                    {latinRomanTooltip}
                                </div>
                                {surnameFirstLocale.includes(locale) === true && (
                                    <div className="ulx-form__form__name">
                                        <div className="ulx-form__form__name__firstFill" {...GLANCE_MASKED}>
                                            {getFormInput('latinLast-name', lastNameLabel, 'text', latinLastName, handleLatinLastNameChange, llnValidationMessage, handleLatinLastNameBlur)}
                                        </div>
                                        <div className="ulx-form__form__name__lastFill">
                                            {getFormInput('latinFirst-name', firstNameLabel, 'text', latinFirstName, handleLatinFirstNameChange, lfnValidationMessage, handleLatinFirstNameBlur)}
                                        </div>
                                    </div>
                                )}
                                {surnameFirstLocale.includes(locale) === false && (
                                    <div className="ulx-form__form__name" {...GLANCE_MASKED}>
                                        <div className="ulx-form__form__name__firstFill">
                                            {getFormInput('latinFirst-name', firstNameLabel, 'text', latinFirstName, handleLatinFirstNameChange, lfnValidationMessage, handleLatinFirstNameBlur)}
                                        </div>
                                        <div className="ulx-form__form__name__lastFill">
                                            {getFormInput('latinLast-name', lastNameLabel, 'text', latinLastName, handleLatinLastNameChange, llnValidationMessage, handleLatinLastNameBlur)}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    <PhoneInputWrapper />
                    { emailField }
                    {
                        features.isFrboFlow && (
                            <>
                                { passwordField(passwordValidationMessage) }
                                <NewsLetter />
                            </>
                        )
                    }
                </div>
                {
                    isEstimateHidden && (
                        <>
                            <div className="ulx-estimate__lyp">
                                <h3>
                                    {parse(propertyCreationText)}
                                </h3>
                            </div>

                            <CompetitorLink
                                {...competitorLink}
                            />
                        </>
                    )
                }
                <LeadFormSubmit
                    {...props}
                    isFormDataValid={isFormDataValid({ ...props })}
                    type={contactType}
                />
            </div>
            { /* eslint-disable-next-line react/no-danger */}
            <span className="ulx-form__terms" dangerouslySetInnerHTML={{ __html: termsText }} />
        </form>
    );
}

const ValidationObject = {
    isValid: PropTypes.bool.isRequired,
    error: PropTypes.string
};

LeadForm.propTypes = {
    propertyCreationText: PropTypes.string.isRequired,
    competitorLink: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        fieldLabel: PropTypes.string,
        display: PropTypes.bool.isRequired
    }).isRequired,
    isDoubledValidation: PropTypes.bool.isRequired,
    title: PropTypes.string.isRequired,
    pageTitle: PropTypes.string.isRequired,
    existingUserText: PropTypes.string.isRequired,
    loginText: PropTypes.string.isRequired,
    newAccountText: PropTypes.string.isRequired,
    firstNameLabel: PropTypes.string.isRequired,
    lastNameLabel: PropTypes.string.isRequired,
    phoneCountryLabel: PropTypes.string.isRequired,
    phoneNumberLabel: PropTypes.string.isRequired,
    emailLabel: PropTypes.string.isRequired,
    password: PropTypes.string.isRequired,
    passwordLabel: PropTypes.string.isRequired,
    newsLetterText: PropTypes.string.isRequired,
    estimateButtonLabel: PropTypes.string.isRequired,
    termsText: PropTypes.string.isRequired,
    referrer: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    emailValidation: PropTypes.shape(ValidationObject).isRequired,
    initialEmailValidation: PropTypes.bool.isRequired,
    setEmail: PropTypes.func.isRequired,
    setPassword: PropTypes.func.isRequired,
    passwordValidation: PropTypes.shape(ValidationObject).isRequired,
    titleName: PropTypes.string,
    setTitleName: PropTypes.func.isRequired,
    firstName: PropTypes.string.isRequired,
    firstNameValidation: PropTypes.shape(ValidationObject).isRequired,
    initialFirstNameValidation: PropTypes.bool.isRequired,
    setFirstName: PropTypes.func.isRequired,
    latinFirstName: PropTypes.string.isRequired,
    latinFirstNameValidation: PropTypes.shape(ValidationObject).isRequired,
    initialLatinFirstNameValidation: PropTypes.bool.isRequired,
    setLatinFirstName: PropTypes.func.isRequired,
    latinLastName: PropTypes.string.isRequired,
    latinLastNameValidation: PropTypes.shape(ValidationObject).isRequired,
    initialLatinLastNameValidation: PropTypes.bool.isRequired,
    setLatinLastName: PropTypes.func.isRequired,
    lastName: PropTypes.string.isRequired,
    lastNameValidation: PropTypes.shape(ValidationObject).isRequired,
    initialLastNameValidation: PropTypes.bool.isRequired,
    setLastName: PropTypes.func.isRequired,
    phoneCountryCode: PropTypes.string.isRequired,
    phoneCountryCodeDigits: PropTypes.string.isRequired,
    setPhoneCountryCode: PropTypes.func.isRequired,
    setPhoneCountryCodeDigits: PropTypes.func.isRequired,
    phoneNumber: PropTypes.string.isRequired,
    setPhoneNumber: PropTypes.func.isRequired,
    phoneValidation: PropTypes.shape(ValidationObject).isRequired,
    setPhoneValidation: PropTypes.func.isRequired,
    phoneInvalidText: PropTypes.string.isRequired,
    showLeadCreationError: PropTypes.bool.isRequired,
    setPublicUuid: PropTypes.func.isRequired,
    setEntryTicketId: PropTypes.func.isRequired,
    validateEmail: PropTypes.func.isRequired,
    validatePhone: PropTypes.func.isRequired,
    isServerEmailValidationEnabled: PropTypes.bool,
    emailValidationErrorText: PropTypes.string.isRequired,
    emailDuplicateErrorText: PropTypes.string.isRequired,
    emailManagedErrorText: PropTypes.string.isRequired,
    setMarketingEmailSubscription: PropTypes.func.isRequired,
    emailOptOut: PropTypes.bool.isRequired,
    authUrl: PropTypes.string.isRequired,
    fingerprint: PropTypes.shape({
        orgId: PropTypes.string,
        deviceId: PropTypes.string
    }).isRequired,
    clearAccountCreationError: PropTypes.func.isRequired,
    shouldShowTitleDropdown: PropTypes.bool,
    step: PropTypes.number.isRequired,
    features: PropTypes.shape({
        isFrboFlow: PropTypes.bool,
        isConventionalLodgingFlow: PropTypes.bool
    }).isRequired,
    latinRomanTooltip: PropTypes.string.isRequired,
    analytics: PropTypes.shape({
        viewTTIstart: PropTypes.func,
        viewTTIstop: PropTypes.func,
        setAnalyticsPageName: PropTypes.func
    }),
    locale: PropTypes.string.isRequired,
    titleTooltip: PropTypes.string.isRequired,
    titleComponent: PropTypes.func.isRequired,
    setEligibilityStatus: PropTypes.func.isRequired,
    propertyName: PropTypes.string,
    pageFlow: PropTypes.shape({
        navigate: PropTypes.func
    })
};

LeadForm.defaultProps = {
    isServerEmailValidationEnabled: false,
    shouldShowTitleDropdown: false,
    titleName: undefined,
    // Calls to analytics.* sit in a synchronous mount effect; a throw there
    // propagates through commit and unmounts the React root.
    analytics: noopAnalytics,
    propertyName: '',
    pageFlow: { navigate: () => {} }
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch) => ({
    setEmail: (email) => dispatch(LeadActions.setEmail(email)),
    setTitleName: (titleName) => dispatch(LeadActions.setTitleName(titleName)),
    setFirstName: (firstName) => dispatch(LeadActions.setFirstName(firstName)),
    setLastName: (lastName) => dispatch(LeadActions.setLastName(lastName)),
    setLatinFirstName: (latinFirstName) => dispatch(LeadActions.setLatinFirstName(latinFirstName)),
    setLatinLastName: (latinLastName) => dispatch(LeadActions.setLatinLastName(latinLastName)),
    setPassword: (password) => dispatch(LeadActions.setPassword(password)),
    setPhoneCountryCode: (code) => dispatch(LeadActions.setPhoneCountryCode(code)),
    setPhoneCountryCodeDigits: (code) => dispatch(LeadActions.setPhoneCountryCodeDigits(code)),
    setPhoneNumber: (number) => dispatch(LeadActions.setPhoneNumber(number)),
    setPhoneValidation: (result) => dispatch(LeadActions.setPhoneValidation(result)),
    setPublicUuid: (publicUuid) => dispatch(LeadActions.setPublicUuid(publicUuid)),
    setEntryTicketId: (entryTicketId) => dispatch(LeadActions.setEntryTicketId(entryTicketId)),
    setAutoAuthUrl: (autoAuthUrl) => dispatch(LeadActions.setAutoAuthUrl(autoAuthUrl)),
    setNewSupplier: (newSupplier) => dispatch(LeadActions.setNewSupplier(newSupplier)),
    onSubmitError: () => dispatch(LeadActions.setLeadCreationError(true)),
    clearAccountCreationError: () => dispatch(LeadActions.setLeadCreationError(false)),
    validateEmail: (email, isDoubledValidation, isServerEmailValidationEnabled) => (
        dispatch(LeadActions.validateEmail(
            email,
            isDoubledValidation,
            isServerEmailValidationEnabled
        ))
    ),
    validatePhone: (phoneNumber) => dispatch(LeadActions.validatePhone(phoneNumber)),
    setMarketingEmailSubscription: (isOptedOut) => dispatch(LeadActions
        .setMarketingEmailSubscription(isOptedOut)),
    setHasAdvertiserForSite: (hasAdvertiserForSite) => dispatch(AppActions
        .setHasAdvertiserForSite(hasAdvertiserForSite)),
    setUserLoggedIn: (isLoggedIn) => dispatch(AppActions.setUserLoggedIn(isLoggedIn)),
    setLoggedInUserType: (type) => dispatch(AppActions.setLoggedInUserType(type)),
    setEligibilityStatus: (reason) => dispatch(LeadActions.setEligibilityStatus(reason))
});

/* istanbul ignore next */
const mapStateToProps = (state) => {
    const { app, lead, joinDetails } = state;
    const {
        email, emailValidation, titleName, firstName, firstNameValidation, lastName,
        lastNameValidation, latinFirstName, latinFirstNameValidation, latinLastName,
        latinLastNameValidation, password, passwordValidation, phoneCountryCode,
        phoneCountryCodeDigits, phoneNumber, phoneValidation, showLeadCreationError,
        emailOptOut, explicitlyChosen
    } = lead || {};
    const { features, brand } = app;
    const {
        propertyName = '',
        latinPropertyName = '',
        propertyNameValidation = { isValid: false },
        latinPropertyNameValidation = { isValid: false }
    } = joinDetails || {};
    const initialFirstNameValidation = !!firstName;
    const initialLatinFirstNameValidation = !!latinFirstName;
    const initialLastNameValidation = !!lastName;
    const initialLatinLastNameValidation = !!latinLastName;
    const initialEmailValidation = !!email;
    const translations = new TranslationUtil(app.translations, app.fallbackTranslations);
    const getTranslation = (suffix, params) => translations.getTranslation(`lyp.leadForm.${suffix}`, params);

    const {
        rootPath, locale, fingerprint, siteUrls, siteName
    } = app;

    const experimentUtil = new ExperimentUtil(app.experiments);
    const isDoubledValidation = features.isConventionalLodgingFlow;
    const contentMessagingUpdatesEnabled = isEnUsPermanentFeatureVrEnabled(experimentUtil);

    const abovCookie = getAbovCookie();
    const abov = abovCookie ? `?abov=${abovCookie}` : '';

    const getAuthUrl = () => {
        if (features.isFrboFlow) {
            const loginInterstitialLink = `/${locale.display}/${rootPath}/login-interstitial${abov}`;
            return `/login?enable_login=true&referrer_page_location=homepage&redirectTo=${loginInterstitialLink}`;
        }
        // Prefer an explicit app-provided currentPath when available (server-side
        // rendering or deterministic testing). Fall back to constructing the path
        // from app.rootPath, or finally to the default list path.
        let currentPath = '';
        if (app && app.currentPath) {
            const { currentPath: appCurrentPath } = app;
            currentPath = appCurrentPath;
        } else if (app && app.rootPath) {
            // Use helper to construct canonical path and sanitize rootPath
            // so server-side rendering and tests remain deterministic.
            currentPath = buildAddPropertyPath(locale.display, app.rootPath, abov);
        } else {
            currentPath = buildAddPropertyPath(locale.display, 'list', abov);
        }
        return currentPath;
    };

    const authUrl = getAuthUrl();

    const links = {
        terms: siteUrls?.ownerTermsAndConditionsUrl || siteUrls?.termsAndConditionsUrl,
        privacy: siteUrls?.privacyPolicyUrl
    };
    const step = 3;
    const emailDuplicateErrorText = `<span>${translations.getTranslation('validation.error.isDuplicateEmail', { authUrl })}</span>`;
    const emailManagedErrorText = `<span>${translations.getTranslation('validation.error.isManagedEmail', { authUrl })}</span>`;

    const options = {
        replace: ({ attribs, name, children }) => {
            if (!attribs) return;
            if (name === 'a') {
                // eslint-disable-next-line consistent-return
                return (
                    <a href={attribs.href}>
                        {domToReact(children)}
                        <Icon
                            after
                            inline
                            inlineIconSize="fds-icon-inline-300"
                            name="open-in-new"
                        />
                    </a>
                );
            }
        }
    };
    const title = getTranslation(contentMessagingUpdatesEnabled ? 'titleTest' : 'title');
    const titleTooltip = getTranslation('title.tooltip');

    const titleComponent = () => ((brand === 'expedia' || contentMessagingUpdatesEnabled)
        ? (<h2 className="ulx-form__title">{title}</h2>)
        : (
            <h2>
                <Popover
                    ariaLabel="Interactive button with icon"
                    label={title}
                    labelClasses="ulx-form__title"
                    popoverSize="fds-popover-sm"
                    showArrow
                    triggerIcon={<Icon name="info-outline" />}
                    wrapAlone
                    nested
                >
                    <PopoverBody
                        classes={classnames('info-popover')}
                    >
                        {parse(titleTooltip, options)}
                    </PopoverBody>
                </Popover>
            </h2>
        ));

    const brandName = capitalizeBrand(brand);

    const competitorLink = {
        title: translations.getTranslation('Vrbo.lyp.estimate.competitor.link.title'),
        description: translations.getTranslation('Vrbo.lyp.estimate.competitor.link.description'),
        fieldLabel: translations.getTranslation('Vrbo.lyp.estimate.competitor.link.fieldLabel'),
        display: true
    };
    return {
        propertyCreationText: translations.getTranslation('Vrbo.lyp.estimate.annualEstimateStartTest'),
        competitorLink,
        isDoubledValidation,
        titleComponent,
        pageTitle: translations.getTranslation('joinForm.pageTitle'),
        latinRomanTooltip: getTranslation('latinRomanTooltip'),
        existingUserText: getTranslation('existingUser.text'),
        loginText: getTranslation(features.isConventionalLodgingFlow ? 'hotelOnboarding.leadForm.description' : 'existingUser.login', { authUrl }),
        newAccountText: getTranslation('existingUser.newAccount', { brandName }),
        firstNameLabel: getTranslation('firstName.label'),
        lastNameLabel: getTranslation('lastName.label'),
        phoneCountryLabel: getTranslation('phoneCountry.label'),
        phoneNumberLabel: getTranslation('phoneNumber.label'),
        emailLabel: getTranslation('email.label'),
        passwordLabel: getTranslation('password.label'),
        newsLetterText: getTranslation('newsletter'),
        estimateButtonLabel: translations.getTranslation('Vrbo.cta.next'),
        termsText: translations.getTranslation(
            features.isConventionalLodgingFlow ? 'CL.leadForm.terms' : 'Vrbo.lyp.leadForm.terms',
            links
        ),
        referrer: app.referrer,
        locale: app.locale?.canonical,
        displayLocale: app.locale.display,
        email,
        emailValidation,
        initialEmailValidation,
        emailDuplicateErrorText,
        emailManagedErrorText,
        emailValidationErrorText: translations.getTranslation('validation.error.isValidEmail'),
        titleName,
        firstName,
        firstNameValidation,
        initialFirstNameValidation,
        lastName,
        lastNameValidation,
        initialLastNameValidation,
        latinFirstName,
        latinFirstNameValidation,
        initialLatinFirstNameValidation,
        latinLastName,
        latinLastNameValidation,
        initialLatinLastNameValidation,
        password,
        passwordValidation,
        phoneCountryCode,
        phoneCountryCodeDigits,
        phoneNumber,
        phoneValidation,
        phoneInvalidText: translations.getTranslation(PHONE_VALIDATION_ERROR_MESSAGE),
        showLeadCreationError,
        isServerEmailValidationEnabled: features.isServerEmailValidationEnabled,
        emailOptOut: getOptOutState(siteName, emailOptOut, explicitlyChosen),
        authUrl,
        fingerprint,
        shouldShowTitleDropdown: siteName === 'homeaway_de',
        step,
        features,
        rootPath,
        propertyName,
        latinPropertyName,
        propertyNameValidation,
        latinPropertyNameValidation
    };
};

export { mapStateToProps };

const ConnectedLeadForm = connect(mapStateToProps, mapDispatchToProps)(LeadForm);
ConnectedLeadForm.displayName = 'LeadForm';
export default hot(module)(withPageFlow(ConnectedLeadForm));
